import { useLayoutEffect } from 'react'
import type { ShopTheme } from './api/types'

/**
 * Das Aussehen des Ladens auf das Dokument bringen.
 *
 * Gesetzt werden nur die --laden-*-Variablen am Wurzelelement; welche Regel
 * daraus welche Farbe macht, steht weiter in styles.css. Das ist der Grund für
 * die zweite Ebene: Schriebe man --akzent direkt ans Element, gewänne dieser
 * Wert gegen jede Medienabfrage, und der Dunkelmodus bekäme die helle Farbe.
 * So bedient ein Satz Werte beide Modi, ohne dass es die Regeln doppelt gibt.
 *
 * useLayoutEffect und nicht useEffect: Der Aufruf sitzt dort, wo die
 * Grunddaten ankommen — also unmittelbar vor dem ersten Zeichnen des Ladens.
 * useEffect liefe erst danach, und genau dann sähe man für ein Bild lang die
 * Vorlagenfarbe.
 */
export function useLadenAussehen(theme: ShopTheme | undefined): void {
  useLayoutEffect(() => {
    if (!theme) return

    const wurzel = document.documentElement
    const gesetzt: string[] = []

    const setze = (name: string, wert: string | null) => {
      if (!wert) return
      wurzel.style.setProperty(name, wert)
      gesetzt.push(name)
    }

    setze('--laden-akzent', farbe(theme.accent))
    setze('--laden-akzent-schrift', farbe(theme.accentText))
    setze('--laden-akzent-dunkel', farbe(theme.accentDark))
    setze('--laden-akzent-schrift-dunkel', farbe(theme.accentTextDark))
    setze('--laden-radius', radius(theme.radius))
    setze('--laden-schriftart', schriftfolge(theme.fontBody))
    setze('--laden-schriftart-ueberschrift', schriftfolge(theme.fontHeading))

    return () => {
      for (const name of gesetzt) wurzel.style.removeProperty(name)
    }
  }, [theme])

  useLayoutEffect(() => {
    const adresse = theme?.faviconUrl
    if (!adresse) return

    // Ein vorhandenes Symbol wird umgehängt statt ergänzt: Zwei <link
    // rel="icon"> nebeneinander sind kein Fehler, aber welches der Browser
    // nimmt, entscheidet er selbst — und dann zeigt der Laden mal das eine,
    // mal das andere.
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="icon"]')
    const neu = !link
    if (!link) {
      link = document.createElement('link')
      link.setAttribute('rel', 'icon')
      document.head.appendChild(link)
    }
    link.setAttribute('href', adresse)

    return () => {
      if (neu) link?.remove()
    }
  }, [theme?.faviconUrl])
}

/**
 * Lässt nur durch, was eine Farbe sein kann.
 *
 * Nicht, weil der Server lügt — er normalisiert alles. Sondern weil ein
 * Aussehen nie mehr tun darf, als Werte zu setzen: Was hier ankommt, stammt
 * aus einem Eingabefeld im Backoffice, und die Kette dorthin ist lang.
 */
function farbe(wert: string | undefined): string | null {
  // Fehlend ist erlaubt: Ladenfenster und Server werden getrennt ausgeliefert,
  // und ein neues Fenster auf einem älteren Server kennt manche Felder noch
  // nicht. Dann bleibt der Rückfall aus dem Stylesheet stehen.
  if (!wert) return null
  return /^#[0-9a-f]{3,8}$/i.test(wert.trim()) ? wert.trim() : null
}

function radius(wert: number): string | null {
  if (!Number.isFinite(wert)) return null
  // Dieselben Grenzen wie im Server: Über 24 wird aus einer Produktkachel ein
  // Kreis, und eine negative Rundung kennt CSS nicht.
  return `${Math.min(24, Math.max(0, Math.round(wert)))}px`
}

/**
 * Eine CSS-Schriftfolge, wie der Server sie schickt.
 *
 * Geprüft wird nur, dass darin nichts steht, was aus einer Wertangabe
 * ausbräche. Eine eigene Liste erlaubter Schriften führt das Ladenfenster
 * nicht: Welche es gibt, entscheidet der Server, und zwei Listen laufen
 * auseinander.
 */
function schriftfolge(wert: string): string | null {
  const folge = wert.trim()
  if (!folge || folge.length > 200) return null
  if (/[;{}<>()\\]/.test(folge)) return null
  return folge
}
